// @ts-strict-ignore
import List from 'antd/lib/list';
import Collapse from 'antd/lib/collapse';
import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectDestinationInfoByDestinationStepConfigId,
    selectNotificationIntegration,
    selectNotifications,
    selectOrgProvidersById,
    selectStepConfigDataFromStepGraphs,
    selectWorkflowId,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { AppComponentProps } from '../../typescriptHelpers';
import { Destination } from '@/types/destinations';
import styles from '../styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import NotificationSettingsModal from './NotificationSettingsModal';
import NotificationStepConfigOverrideRow from './NotificationStepConfigOverrideRow';
import { deriveOverrideEntries, notificationRowIndicators } from '../../helpers/notificationOverrideHelpers';
import Search from 'antd/lib/input/Search';
import NotificationRow from '../common/NotificationRow';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { createUserSettingsRoute } from '@/helpers/routeHelpers';
import { UserSettingsPageView } from '@/components/userSettings/UserSettingsComponent';
import { SortTableButton } from '@/components/tableFilters/SortTableButton';
import { SettingsSortType } from '@/types/sorts';
import { AppNotificationType, CommonNotification, StepNotificationConfigOverride } from '../../types';
import {
    BorderOutlined,
    CaretRightOutlined,
    CheckCircleOutlined,
    CheckSquareOutlined,
    CloseCircleOutlined,
    CloseOutlined,
    ControlOutlined,
    SlidersOutlined,
    HistoryOutlined,
    NumberOutlined,
    SortAscendingOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    BulkUpdateNotificationsEnabledRequest,
    tryUpdateAppSettings,
    tryUpdateAppSettingsV2,
    UpdateStepNotificationConfigsRequest,
} from '../../actions/appSettings';
import { AppSettingsUpdatable, AppSettingsV2Updatable } from '../../actions/updatable';
import { Checkbox, Flex } from 'antd/lib';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '../../utils/globalMessage';

const CARET_SLOT_WIDTH = 24;

interface AppNotificationsProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
}

const AppNotifications: React.FC<AppNotificationsProps> = (props: AppNotificationsProps) => {
    const { appId, orgId } = props;
    const dispatch = useDispatch();
    const notifications = useSelector((state: RootState) => selectNotifications(state, appId));
    const stepGraphStepsMap = useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, appId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const destinationInfoByDestinationStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );

    const destinationsByStepConfigId = React.useMemo<Record<string, Destination[]>>(() => {
        if (!stepGraphStepsMap) return {};
        const result: Record<string, Destination[]> = {};
        for (const [stepConfigId, step] of Object.entries(stepGraphStepsMap)) {
            result[stepConfigId] = step.destinationStepConfigIds
                .map((id) => destinationInfoByDestinationStepConfigId[id])
                .filter((d): d is Destination => d !== undefined);
        }
        return result;
    }, [stepGraphStepsMap, destinationInfoByDestinationStepConfigId]);

    const availableChannelsById = useSelector((state: RootState) => {
        const integration = selectNotificationIntegration(state, appId);
        if (!integration) return {} as Record<string, string>;
        const providersById = selectOrgProvidersById(state, orgId);
        const provider = providersById[integration.providerId];
        const channels = provider?.data?.availableChannels ?? [];
        const map: Record<string, string> = {};
        for (const c of channels as Array<{ id: string; name: string }>) {
            map[c.id] = c.name;
        }
        return map;
    });

    const primaryChannelNames = useSelector((state: RootState) => {
        const integration = selectNotificationIntegration(state, appId);
        const data = integration?.data as { primaryChannels?: Array<{ name: string }> } | undefined;
        return (data?.primaryChannels ?? []).map((c) => c.name);
    });

    const threadsReleaseNotifications = useSelector((state: RootState) => {
        const integration = selectNotificationIntegration(state, appId);
        const data = integration?.data as { threadReleaseNotifications?: boolean } | undefined;
        return data?.threadReleaseNotifications ?? false;
    });

    const BULK_LOADING_KEY = 'bulk-notifications-enabled';
    const [isSelectMode, setIsSelectMode] = React.useState(false);
    const [selectedNotificationIds, setSelectedNotificationIds] = React.useState<Record<string, boolean>>({});
    const selectedCount = Object.keys(selectedNotificationIds).length;
    const bulkIsLoading = useIsLoading(AppSettingsUpdatable.BulkUpdateNotificationsEnabled, BULK_LOADING_KEY);
    const bulkSuccess = useUpdated(AppSettingsUpdatable.BulkUpdateNotificationsEnabled, BULK_LOADING_KEY);
    const bulkError = useError(AppSettingsUpdatable.BulkUpdateNotificationsEnabled, BULK_LOADING_KEY);

    const lastBulkRequestRef = React.useRef<{ count: number; enabled: boolean } | null>(null);
    React.useEffect(() => {
        if (bulkSuccess) {
            const lastRequest = lastBulkRequestRef.current;
            if (lastRequest) {
                const noun = lastRequest.count === 1 ? 'notification' : 'notifications';
                globalMessage.success(`${lastRequest.count} ${noun} ${lastRequest.enabled ? 'enabled' : 'disabled'}!`);
            }
            setIsSelectMode(false);
            setSelectedNotificationIds({});
        }
    }, [bulkSuccess]);

    React.useEffect(() => {
        if (bulkError) {
            globalMessage.error(bulkError);
        }
    }, [bulkError]);

    const toggleNotificationSelected = (notificationId: string) => {
        setSelectedNotificationIds((prev) => {
            const next = { ...prev };
            if (next[notificationId]) {
                delete next[notificationId];
            } else {
                next[notificationId] = true;
            }
            return next;
        });
    };

    const exitSelectMode = () => {
        setIsSelectMode(false);
        setSelectedNotificationIds({});
    };

    const handleBulkSetEnabled = (enabled: boolean) => {
        const notificationIds = Object.keys(selectedNotificationIds);
        if (notificationIds.length === 0) {
            return;
        }
        setOptimistic((prev) => {
            const next = { ...prev };
            const setOptimisticKey = (key: string) => {
                next[key] = enabled;
                optimisticRequestKeyRef.current[key] = BULK_LOADING_KEY;
            };
            notificationIds.forEach((id) => {
                setOptimisticKey(id);
                const overrides = notifications.find((n) => n.id === id)?.stepConfigOverrides ?? [];
                overrides.forEach((o) => setOptimisticKey(`${id}#${o.stepConfigId}`));
            });
            return next;
        });
        lastBulkRequestRef.current = { count: notificationIds.length, enabled };
        const request: BulkUpdateNotificationsEnabledRequest = { orgId, appId, notificationIds, enabled };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.BulkUpdateNotificationsEnabled, request, BULK_LOADING_KEY));
    };

    const [showNotificationSettings, setShowNotificationSettings] = React.useState(false);
    const [notificationIdSelectedForSettings, setNotificationIdSelectedForSettings] = React.useState(null);
    const [overrideTarget, setOverrideTarget] = React.useState<{ stepConfigId?: string; createNew?: boolean } | null>(
        null,
    );
    const [searchValue, setSearchValue] = React.useState('');
    const [selectedSortType, setSelectedSortType] = React.useState<SettingsSortType>(SettingsSortType.Name);

    const openNotificationDrawer = (
        notificationId: string,
        target: { stepConfigId?: string; createNew?: boolean } | null = null,
    ) => {
        setNotificationIdSelectedForSettings(notificationId);
        setOverrideTarget(target);
        setShowNotificationSettings(true);
    };

    const history = useHistory();
    const location = useLocation();

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const idFromQuery = params.get('notificationId');
        if (idFromQuery) {
            const stepConfigIdFromQuery = params.get('stepConfigId');
            if (stepConfigIdFromQuery && stepGraphStepsMap) {
                const stepGraphStep = stepGraphStepsMap[stepConfigIdFromQuery];
                const notification = notifications.find((n) => n.id === idFromQuery);
                const override =
                    notification?.stepConfigOverrides?.find((o) => o.stepConfigId === stepConfigIdFromQuery) ?? null;
                if (stepGraphStep && override) {
                    openNotificationDrawer(idFromQuery, { stepConfigId: stepConfigIdFromQuery });
                    history.replace({ search: null });
                    return;
                }
            }
            // Default: open app-level notification settings
            openNotificationDrawer(idFromQuery);
            history.replace({ search: null });
        }
    }, [location.search, stepGraphStepsMap, notifications]);

    // Single source of truth for optimistic toggle state.
    const [optimistic, setOptimistic] = React.useState<Record<string, boolean>>({});

    // Tracks if we've seen isLoading=true for each key (avoids clearing before saga starts).
    const sawLoadingRef = React.useRef<Record<string, boolean>>({});

    const optimisticRequestKeyRef = React.useRef<Record<string, string>>({});

    // Keys of in-flight notification update requests (covers both app-level and per-step-override endpoints).
    const loadingKeys = useSelector(
        (state: RootState) =>
            state.ui.loading
                .filter(
                    (l) =>
                        l.actionName === AppSettingsUpdatable.UpdateNotificationSetting ||
                        l.actionName === AppSettingsUpdatable.BulkUpdateNotificationsEnabled ||
                        l.actionName === AppSettingsV2Updatable.UpdateStepNotificationConfigs,
                )
                .map((l) => l.id as string),
        shallowEqual,
    );

    const rowRequestInFlight = loadingKeys.some((key) => key !== BULK_LOADING_KEY);

    // Clear an optimistic entry once its dispatch has completed (loading went true → false).
    React.useEffect(() => {
        const loadingSet = new Set(loadingKeys);
        setOptimistic((prev) => {
            const keys = Object.keys(prev);
            if (keys.length === 0) return prev;
            const next = { ...prev };
            let changed = false;
            for (const key of keys) {
                if (loadingSet.has(optimisticRequestKeyRef.current[key] ?? key)) {
                    sawLoadingRef.current[key] = true;
                } else if (sawLoadingRef.current[key]) {
                    delete next[key];
                    delete sawLoadingRef.current[key];
                    delete optimisticRequestKeyRef.current[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [loadingKeys]);

    const handleToggle = (
        notification: CommonNotification,
        clickedOverride: StepNotificationConfigOverride | null,
        newValue: boolean,
    ) => {
        const overrides = notification.stepConfigOverrides ?? [];

        const childTargetState: Record<string, boolean> = {};
        for (const o of overrides) {
            const inClickedGroup = !!clickedOverride?.groupName && o.groupName === clickedOverride.groupName;
            if (clickedOverride == null || o.stepConfigId === clickedOverride.stepConfigId || inClickedGroup) {
                childTargetState[o.stepConfigId] = newValue;
            } else {
                childTargetState[o.stepConfigId] = o.enabled;
            }
        }

        const allChildrenMatchNewValue = overrides.every((o) => childTargetState[o.stepConfigId] === newValue);
        const parentTarget: boolean | null =
            clickedOverride == null ? newValue
            : allChildrenMatchNewValue ? newValue
            : null;

        const requestKey =
            parentTarget != null || !clickedOverride ?
                notification.id
            :   `${notification.id}#${clickedOverride.stepConfigId}`;

        setOptimistic((prev) => {
            const next = { ...prev };
            const setOptimisticKey = (key: string, value: boolean) => {
                next[key] = value;
                optimisticRequestKeyRef.current[key] = requestKey;
            };
            if (parentTarget != null) setOptimisticKey(notification.id, parentTarget);
            for (const o of overrides) {
                if (
                    childTargetState[o.stepConfigId] !== o.enabled ||
                    o.stepConfigId === clickedOverride?.stepConfigId
                ) {
                    setOptimisticKey(`${notification.id}#${o.stepConfigId}`, childTargetState[o.stepConfigId]);
                }
            }
            return next;
        });

        if (parentTarget != null) {
            const request: BulkUpdateNotificationsEnabledRequest = {
                orgId,
                appId,
                notificationIds: [notification.id],
                enabled: parentTarget,
            };
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.BulkUpdateNotificationsEnabled, request, requestKey));
            return;
        }

        const changedStepConfigIds = overrides
            .filter((o) => childTargetState[o.stepConfigId] !== o.enabled)
            .map((o) => o.stepConfigId);
        if (workflowId && changedStepConfigIds.length > 0) {
            const request: UpdateStepNotificationConfigsRequest = {
                orgId,
                appId,
                workflowId,
                notificationId: notification.id,
                stepConfigIds: changedStepConfigIds,
                enabled: newValue,
            };
            dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.UpdateStepNotificationConfigs, request, requestKey));
        }
    };

    const computeSwitchState = (notification: CommonNotification): 'on' | 'off' | 'indeterminate' => {
        const parentEnabled = optimistic[notification.id] ?? notification.enabled;
        const overrides = notification.stepConfigOverrides ?? [];
        if (overrides.length === 0) return parentEnabled ? 'on' : 'off';
        const childStates = overrides.map((o) => optimistic[`${notification.id}#${o.stepConfigId}`] ?? o.enabled);
        const all = [parentEnabled, ...childStates];
        if (all.every((s) => s === true)) return 'on';
        if (all.every((s) => s === false)) return 'off';
        return 'indeterminate';
    };

    const processedNotifications = React.useMemo(() => {
        const filtered = notifications.filter((notif) =>
            notif.displayText.toLowerCase().includes(searchValue.toLowerCase()),
        );
        const indicatorsById = new Map(filtered.map((notif) => [notif.id, notificationRowIndicators(notif)]));
        const byIndicatorThenName = (
            a: CommonNotification,
            b: CommonNotification,
            indicator: keyof ReturnType<typeof notificationRowIndicators>,
        ) => {
            const aHas = indicatorsById.get(a.id)?.[indicator];
            if (aHas === indicatorsById.get(b.id)?.[indicator]) return a.displayText.localeCompare(b.displayText);
            return aHas ? -1 : 1;
        };

        return [...filtered].sort((a, b) => {
            switch (selectedSortType) {
                case SettingsSortType.Name:
                    return a.displayText.localeCompare(b.displayText);

                case SettingsSortType.EnabledFirst:
                    if (a.enabled === b.enabled) return a.displayText.localeCompare(b.displayText);
                    return a.enabled ? -1 : 1;

                case SettingsSortType.ChannelOverridesFirst:
                    return byIndicatorThenName(a, b, 'hasChannelOverride');

                case SettingsSortType.UserOverridesFirst:
                    return byIndicatorThenName(a, b, 'hasMentionOverride');

                case SettingsSortType.TriggerOverridesFirst:
                    return byIndicatorThenName(a, b, 'hasTriggerOverride');

                case SettingsSortType.SettingsOverridesFirst:
                    return byIndicatorThenName(a, b, 'hasSettingsOverride');

                default:
                    return 0;
            }
        });
    }, [notifications, searchValue, selectedSortType]);

    const allSelected =
        processedNotifications.length > 0 && processedNotifications.every((n) => !!selectedNotificationIds[n.id]);
    const handleToggleSelectAll = () => {
        if (allSelected) {
            setSelectedNotificationIds({});
            return;
        }
        const next: Record<string, boolean> = {};
        processedNotifications.forEach((n) => {
            next[n.id] = true;
        });
        setSelectedNotificationIds(next);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} ref={props.refProp}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <Space direction="vertical" size={4} style={{ flexShrink: 0 }}>
                    <Text style={{ ...styles.appSettingsSectionTitleStyle, whiteSpace: 'nowrap' }}>Notifications</Text>
                    {threadsReleaseNotifications && (
                        <Text type="secondary">
                            Release notifications are threaded under one message per release. Failures are also posted
                            to the channel.
                        </Text>
                    )}
                </Space>
                <Flex gap={8} align="center" style={{ minWidth: 0 }}>
                    <Search
                        placeholder="Search notifications"
                        allowClear
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onSearch={(value) => {
                            setSearchValue(value);
                        }}
                        style={{ flex: '1 1 400px', maxWidth: 400, minWidth: 140 }}
                    />
                    <SortTableButton<SettingsSortType>
                        selectedSortType={selectedSortType}
                        sortOptions={[
                            {
                                sortType: SettingsSortType.Name,
                                text: 'Name',
                                icon: <SortAscendingOutlined style={{ color: runwayColors.primary }} />,
                            },
                            {
                                sortType: SettingsSortType.EnabledFirst,
                                text: 'Enabled',
                                icon: <ControlOutlined style={{ color: runwayColors.primary }} />,
                            },
                            {
                                sortType: SettingsSortType.ChannelOverridesFirst,
                                text: 'Channel overrides',
                                icon: <NumberOutlined style={{ color: runwayColors.primary }} />,
                            },
                            {
                                sortType: SettingsSortType.UserOverridesFirst,
                                text: 'Mention overrides',
                                icon: <TeamOutlined style={{ color: runwayColors.primary }} />,
                            },
                            {
                                sortType: SettingsSortType.TriggerOverridesFirst,
                                text: 'Trigger overrides',
                                icon: <HistoryOutlined style={{ color: runwayColors.primary }} />,
                            },
                            {
                                sortType: SettingsSortType.SettingsOverridesFirst,
                                text: 'Setting overrides',
                                icon: <SlidersOutlined style={{ color: runwayColors.primary }} />,
                            },
                        ]}
                        onSelectSort={(sortType) => {
                            setSelectedSortType(sortType);
                        }}
                    />
                    {isSelectMode && (
                        <>
                            <Divider type="vertical" style={{ height: 20, borderColor: antGrey.grey6, margin: 0 }} />
                            <Button
                                icon={
                                    allSelected ?
                                        <BorderOutlined style={{ color: runwayColors.primary }} />
                                    :   <CheckSquareOutlined style={{ color: runwayColors.primary }} />
                                }
                                onClick={handleToggleSelectAll}
                            >
                                {allSelected ? 'Deselect all' : 'Select all'}
                            </Button>
                            <PermissionedActionButton
                                actionType={RunwayUserAction.UAUpdateAppSettings}
                                disabled={selectedCount === 0 || rowRequestInFlight}
                                loading={bulkIsLoading}
                                icon={
                                    <CheckCircleOutlined
                                        style={{ color: selectedCount === 0 ? undefined : runwayColors.primary }}
                                    />
                                }
                                onClick={() => handleBulkSetEnabled(true)}
                            >
                                Enable
                            </PermissionedActionButton>
                            <PermissionedActionButton
                                actionType={RunwayUserAction.UAUpdateAppSettings}
                                disabled={selectedCount === 0 || rowRequestInFlight}
                                loading={bulkIsLoading}
                                icon={
                                    <CloseCircleOutlined
                                        style={{ color: selectedCount === 0 ? undefined : runwayColors.primary }}
                                    />
                                }
                                onClick={() => handleBulkSetEnabled(false)}
                            >
                                Disable
                            </PermissionedActionButton>
                            <Divider type="vertical" style={{ height: 20, borderColor: antGrey.grey6, margin: 0 }} />
                        </>
                    )}
                    {isSelectMode ?
                        <Button
                            icon={<CloseOutlined style={{ color: runwayColors.statusRed }} />}
                            onClick={exitSelectMode}
                        >
                            Cancel select
                        </Button>
                    :   <Button
                            icon={<CheckSquareOutlined style={{ color: runwayColors.primary }} />}
                            onClick={() => setIsSelectMode(true)}
                        >
                            Select
                        </Button>
                    }
                </Flex>
            </div>
            <List
                header={null}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10, size: 'small', showSizeChanger: false }}
                dataSource={processedNotifications}
                renderItem={(notification) => {
                    const overrides = notification.stepConfigOverrides ?? [];
                    const overrideByStepId = new Map(overrides.map((o) => [o.stepConfigId, o]));
                    const childEntries =
                        stepGraphStepsMap ?
                            deriveOverrideEntries(overrides)
                                .map((entry) => ({
                                    entry,
                                    steps: entry.stepConfigIds
                                        .map((id) => stepGraphStepsMap[id])
                                        .filter((s) => s != null),
                                }))
                                .filter(({ steps }) => steps.length > 0)
                        :   [];
                    const hasChildren = childEntries.length > 0;
                    const loadingSet = new Set(loadingKeys);
                    const isSelected = !!selectedNotificationIds[notification.id];
                    const parentIsUpdating =
                        loadingSet.has(notification.id) ||
                        bulkIsLoading ||
                        overrides.some((o) => loadingSet.has(`${notification.id}#${o.stepConfigId}`));

                    const selectCheckbox = () =>
                        isSelectMode ?
                            <div
                                style={{
                                    width: CARET_SLOT_WIDTH,
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Checkbox
                                    checked={isSelected}
                                    onChange={() => toggleNotificationSelected(notification.id)}
                                />
                            </div>
                        :   null;

                    const notificationRow = (
                        <div style={{ paddingBlock: 12, flex: 1 }}>
                            <NotificationRow
                                notification={notification}
                                notificationsEnabled
                                switchState={computeSwitchState(notification)}
                                isUpdating={parentIsUpdating}
                                onClickSettings={(notificationId) => {
                                    openNotificationDrawer(notificationId);
                                }}
                                onToggleEnabled={(enabled) => handleToggle(notification, null, enabled)}
                                onAddOverride={(notificationId) =>
                                    openNotificationDrawer(notificationId, { createNew: true })
                                }
                                actionType={RunwayUserAction.UAUpdateAppSettings}
                                stepGraphStepsMap={stepGraphStepsMap}
                                destinationsByStepConfigId={destinationsByStepConfigId}
                                platform={platform}
                                appStoreType={appStoreType}
                                availableChannelsById={availableChannelsById}
                                primaryChannelNames={primaryChannelNames}
                            />
                        </div>
                    );

                    if (!hasChildren) {
                        // Reserve the caret slot so rows with and without children line up
                        return (
                            <List.Item
                                key={notification.id}
                                style={{
                                    paddingBlock: 0,
                                    paddingInline: 16,
                                    marginInline: -16,
                                    borderBlockEnd: `1px solid ${antGrey.grey5}`,
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                    {selectCheckbox()}
                                    <div style={{ width: CARET_SLOT_WIDTH, flexShrink: 0 }} />
                                    {notificationRow}
                                </div>
                            </List.Item>
                        );
                    }

                    return (
                        <List.Item
                            key={notification.id}
                            style={{
                                paddingBlock: 0,
                                paddingInline: 16,
                                marginInline: -16,
                                borderBlockEnd: `1px solid ${antGrey.grey5}`,
                            }}
                        >
                            <Collapse
                                className="automations-collapsible notifications-collapsible"
                                defaultActiveKey={[]}
                                style={{ flex: 1 }}
                                ghost
                                expandIcon={({ isActive }) => (
                                    <Flex align="center">
                                        {selectCheckbox()}
                                        <CaretRightOutlined
                                            style={{
                                                color: antGrey.grey6,
                                                fontSize: 14,
                                            }}
                                            rotate={isActive ? 90 : 0}
                                        />
                                    </Flex>
                                )}
                            >
                                <Collapse.Panel header={notificationRow} key="1">
                                    <List
                                        size="small"
                                        dataSource={childEntries}
                                        renderItem={({ entry, steps }) => {
                                            const isGroup = entry.stepConfigIds.length > 1;
                                            const seenDestinations = new Set<Destination>();
                                            const destinations: Destination[] = [];
                                            entry.stepConfigIds.forEach((id) => {
                                                (destinationsByStepConfigId[id] ?? []).forEach((destination) => {
                                                    if (!seenDestinations.has(destination)) {
                                                        seenDestinations.add(destination);
                                                        destinations.push(destination);
                                                    }
                                                });
                                            });
                                            const enabled = entry.stepConfigIds.every(
                                                (id) =>
                                                    optimistic[`${notification.id}#${id}`] ??
                                                    overrideByStepId.get(id)?.enabled,
                                            );
                                            return (
                                                <List.Item
                                                    key={entry.key}
                                                    style={{ padding: 0, borderBottom: `1px solid ${antGrey.grey5}` }}
                                                >
                                                    <NotificationStepConfigOverrideRow
                                                        appId={appId}
                                                        orgId={orgId}
                                                        workflowId={workflowId}
                                                        notificationId={notification.id as AppNotificationType}
                                                        stepGraphStep={steps[0]}
                                                        override={entry.override}
                                                        appNotification={notification}
                                                        group={
                                                            isGroup ?
                                                                {
                                                                    name: entry.groupName || 'Override group',
                                                                    stepConfigIds: entry.stepConfigIds,
                                                                }
                                                            :   undefined
                                                        }
                                                        destinations={destinations}
                                                        enabled={enabled}
                                                        isUpdating={parentIsUpdating}
                                                        platform={platform}
                                                        appStoreType={appStoreType}
                                                        availableChannelsById={availableChannelsById}
                                                        onClickSettings={() => {
                                                            openNotificationDrawer(notification.id, {
                                                                stepConfigId: entry.stepConfigIds[0],
                                                            });
                                                        }}
                                                        onToggleEnabled={(enabled) =>
                                                            handleToggle(notification, entry.override, enabled)
                                                        }
                                                    />
                                                </List.Item>
                                            );
                                        }}
                                    />
                                </Collapse.Panel>
                            </Collapse>
                        </List.Item>
                    );
                }}
            ></List>
            <Text>
                You can enable personal email notifications for any app-level notification from{' '}
                <Link to={createUserSettingsRoute(UserSettingsPageView.Email)}>
                    User settings &gt; Email notifications
                </Link>
                .
            </Text>
            <Text>
                Certain notifications also support email notifications to any address you specify in their respective
                notification settings.
            </Text>
            {notificationIdSelectedForSettings && (
                <NotificationSettingsModal
                    appId={props.appId}
                    orgId={props.orgId}
                    onClose={() => setShowNotificationSettings(false)}
                    open={showNotificationSettings}
                    notificationId={notificationIdSelectedForSettings}
                    initialOverrideTarget={overrideTarget ?? undefined}
                    onAfterClose={() => {
                        setNotificationIdSelectedForSettings(null);
                        setOverrideTarget(null);
                    }}
                />
            )}
        </div>
    );
};

export default AppNotifications;
